import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { branding } from '../constants/branding';
import { FaDice, FaFutbol, FaBroadcastTower, FaSpinner, FaSearch, FaBrain, FaChartLine, FaChevronLeft, FaChevronRight, FaBookmark, FaTrash, FaPlus, FaArrowDown } from 'react-icons/fa';
import { igamingService, type Game, type SportsEvent } from '../services/igamingService';
import { apiService } from '../services/apiService';
import { formatAmount } from '../utils/currency';
import { useAppSelector } from '../store/hooks';
import IGamingAccountModal from '../components/IGamingAccountModal';
import TetrisGame from '../components/TetrisGame';
import '../App.css';

// Premier League Match Interface
interface PremierLeagueMatch {
  id: number;
  homeTeam: string;
  awayTeam: string;
  gameWeek: number;
  date: string;
  time: string;
  venue: string;
  homeTeamStats: {
    position: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    form: string[]; // Last 5 matches: 'W', 'D', 'L'
  };
  awayTeamStats: {
    position: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    form: string[]; // Last 5 matches: 'W', 'D', 'L'
  };
  prediction?: {
    homeWin: number;
    draw: number;
    awayWin: number;
    mostLikely: 'home' | 'draw' | 'away';
    confidence: number;
    predictedScore: string;
  };
  actualResult?: {
    finalScore: string;
    winner: 'home' | 'draw' | 'away';
    matchEvents: string[];
  };
}

const IGaming: React.FC = () => {
  const navigate = useNavigate();
  const profile = useAppSelector((state) => state.profile.profile);
  const [activeTab, setActiveTab] = useState<'casino' | 'sportsbook' | 'live'>('casino');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [casinoGames, setCasinoGames] = useState<Game[]>([]);
  const [sportsEvents, setSportsEvents] = useState<SportsEvent[]>([]);
  const [liveGames, setLiveGames] = useState<any[]>([]);
  const [premierLeagueMatches, setPremierLeagueMatches] = useState<PremierLeagueMatch[]>([]);
  const [currentGameweek, setCurrentGameweek] = useState(11);
  const [savedPredictions, setSavedPredictions] = useState<PremierLeagueMatch[]>([]);
  const [showSavedPredictions, setShowSavedPredictions] = useState(false);
  const [selectedMatchForStats, setSelectedMatchForStats] = useState<PremierLeagueMatch | null>(null);
  const [showPredictionComparison, setShowPredictionComparison] = useState(false);
  
  // iGaming Account states
  const [showIGamingModal, setShowIGamingModal] = useState(false);
  const [igamingAccount, setIGamingAccount] = useState<any>(null);
  const [igamingAccountLoading, setIGamingAccountLoading] = useState(false);
  const [igamingModalType, setIGamingModalType] = useState<'deposit' | 'withdraw'>('deposit');
  const [accounts, setAccounts] = useState<any[]>([]);
  
  // Tetris game state
  const [showTetrisGame, setShowTetrisGame] = useState(false);
  const [tetrisConfig, setTetrisConfig] = useState<any>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch Tetris config
  const fetchTetrisConfig = async () => {
    try {
      const response = await apiService.get('/api/igaming/tetris/config');
      if (response.success) {
        setTetrisConfig(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch Tetris config:', err);
      setTetrisConfig(null);
    }
  };

  // Fetch iGaming account
  const fetchIGamingAccount = async () => {
    if (!profile?.id) return;
    
    setIGamingAccountLoading(true);
    try {
      const response = await apiService.get('/api/igaming-account');
      if (response.success) {
        setIGamingAccount(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch iGaming account:', err);
      setIGamingAccount(null);
    } finally {
      setIGamingAccountLoading(false);
    }
  };

  // Fetch user accounts for deposit/withdraw
  const fetchAccounts = async () => {
    try {
      const response = await apiService.get('/accounts');
      if (response.success && Array.isArray(response.data)) {
        setAccounts(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  // Handle iGaming account transaction success
  const handleIGamingAccountSuccess = () => {
    fetchIGamingAccount();
    setShowIGamingModal(false);
  };

  // Load iGaming account on mount
  useEffect(() => {
    if (profile?.id) {
      fetchIGamingAccount();
      fetchAccounts();
    }
    fetchTetrisConfig(); // Load Tetris config regardless of profile
  }, [profile?.id]);

  // Load saved predictions from database on mount
  useEffect(() => {
    const loadSavedPredictions = async () => {
      try {
        const predictions = await igamingService.getSavedPredictions();
        setSavedPredictions(predictions as any);
      } catch (err) {
        console.error('Failed to load saved predictions:', err);
      }
    };

    loadSavedPredictions();
  }, []);

  // Load data based on active tab
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (activeTab === 'casino') {
          const response = await igamingService.getCasinoGames({
            page: 0,
            size: 100,
            q: searchQuery || undefined
          });
          setCasinoGames(response.content || []);
        } else if (activeTab === 'sportsbook') {
          const response = await igamingService.getSportsEvents({
            page: 0,
            size: 100
          });
          setSportsEvents(response.content || []);
        } else if (activeTab === 'live') {
          const response = await igamingService.getLiveGames({
            page: 0,
            size: 100
          });
          setLiveGames(response.content || []);
        }
      } catch (err: any) {
        console.error('Failed to load iGaming data:', err);
        setError(err.message || 'Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeTab, searchQuery]);

  // Get fixtures for a specific gameweek
  const getFixturesForGameweek = (gameweek: number): PremierLeagueMatch[] => {
    if (gameweek === 11) {
      // Gameweek 11 fixtures (some completed, some upcoming)
      return [
        {
          id: 1,
          homeTeam: 'Tottenham Hotspur',
          awayTeam: 'Manchester United',
          gameWeek: 11,
          date: 'Saturday, 8th November 2025',
          time: '12:30 GMT',
          venue: 'Tottenham Hotspur Stadium',
          homeTeamStats: {
            position: 4,
            wins: 7,
            draws: 2,
            losses: 2,
            goalsFor: 22,
            goalsAgainst: 13,
            form: ['W', 'W', 'D', 'L', 'W']
          },
          awayTeamStats: {
            position: 6,
            wins: 6,
            draws: 2,
            losses: 3,
            goalsFor: 18,
            goalsAgainst: 15,
            form: ['W', 'L', 'W', 'D', 'L']
          },
          actualResult: {
            finalScore: '2-2',
            winner: 'draw',
            matchEvents: [
              'Tottenham took 2-0 lead by 35th minute',
              'Man United mounted impressive comeback in second half',
              'United equalized in 78th minute to rescue a point',
              'Both teams had late chances to win but settled for draw'
            ]
          }
        },
        {
          id: 2,
          homeTeam: 'Sunderland',
          awayTeam: 'Arsenal',
          gameWeek: 11,
          date: 'Saturday, 8th November 2025',
          time: '17:30 GMT',
          venue: 'Stadium of Light',
          homeTeamStats: {
            position: 15,
            wins: 3,
            draws: 3,
            losses: 5,
            goalsFor: 12,
            goalsAgainst: 18,
            form: ['L', 'D', 'W', 'L', 'D']
          },
          awayTeamStats: {
            position: 2,
            wins: 8,
            draws: 3,
            losses: 0,
            goalsFor: 25,
            goalsAgainst: 10,
            form: ['W', 'D', 'W', 'W', 'D']
          },
          actualResult: {
            finalScore: '1-1',
            winner: 'draw',
            matchEvents: [
              'Sunderland\'s passionate home crowd lifted the team',
              'Early goal for Sunderland in 19th minute',
              'Arsenal equalized in 56th minute',
              'Arsenal failed to break down resolute home defense despite 65% possession'
            ]
          }
        },
        {
          id: 3,
          homeTeam: 'Chelsea',
          awayTeam: 'Wolverhampton Wanderers',
          gameWeek: 11,
          date: 'Saturday, 8th November 2025',
          time: '20:00 GMT',
          venue: 'Stamford Bridge',
          homeTeamStats: {
            position: 5,
            wins: 6,
            draws: 3,
            losses: 2,
            goalsFor: 21,
            goalsAgainst: 14,
            form: ['W', 'D', 'W', 'L', 'W']
          },
          awayTeamStats: {
            position: 18,
            wins: 2,
            draws: 3,
            losses: 6,
            goalsFor: 11,
            goalsAgainst: 22,
            form: ['L', 'L', 'D', 'W', 'L']
          }
        },
        {
          id: 4,
          homeTeam: 'Manchester City',
          awayTeam: 'Liverpool',
          gameWeek: 11,
          date: 'Sunday, 9th November 2025',
          time: '16:30 GMT',
          venue: 'Etihad Stadium',
          homeTeamStats: {
            position: 1,
            wins: 9,
            draws: 2,
            losses: 0,
            goalsFor: 28,
            goalsAgainst: 8,
            form: ['W', 'W', 'D', 'W', 'W']
          },
          awayTeamStats: {
            position: 3,
            wins: 7,
            draws: 3,
            losses: 1,
            goalsFor: 23,
            goalsAgainst: 12,
            form: ['W', 'W', 'D', 'W', 'L']
          },
          actualResult: {
            finalScore: '1-1',
            winner: 'draw',
            matchEvents: [
              'Title clash lived up to the hype with end-to-end action',
              'Man City took early lead in 15th minute',
              'Liverpool equalized just before half-time',
              'Both teams had chances but solid defenses held firm'
            ]
          }
        },
        {
          id: 5,
          homeTeam: 'Aston Villa',
          awayTeam: 'AFC Bournemouth',
          gameWeek: 11,
          date: 'Sunday, 9th November 2025',
          time: '14:00 GMT',
          venue: 'Villa Park',
          homeTeamStats: {
            position: 9,
            wins: 5,
            draws: 2,
            losses: 4,
            goalsFor: 15,
            goalsAgainst: 16,
            form: ['L', 'W', 'W', 'L', 'D']
          },
          awayTeamStats: {
            position: 11,
            wins: 4,
            draws: 3,
            losses: 4,
            goalsFor: 14,
            goalsAgainst: 15,
            form: ['W', 'L', 'D', 'W', 'L']
          }
        },
        {
          id: 6,
          homeTeam: 'Brentford',
          awayTeam: 'Newcastle United',
          gameWeek: 11,
          date: 'Sunday, 9th November 2025',
          time: '14:00 GMT',
          venue: 'Brentford Community Stadium',
          homeTeamStats: {
            position: 10,
            wins: 4,
            draws: 4,
            losses: 3,
            goalsFor: 16,
            goalsAgainst: 15,
            form: ['D', 'W', 'D', 'L', 'W']
          },
          awayTeamStats: {
            position: 7,
            wins: 5,
            draws: 4,
            losses: 2,
            goalsFor: 17,
            goalsAgainst: 12,
            form: ['D', 'W', 'D', 'W', 'D']
          }
        },
        {
          id: 7,
          homeTeam: 'Crystal Palace',
          awayTeam: 'Brighton & Hove Albion',
          gameWeek: 11,
          date: 'Sunday, 9th November 2025',
          time: '14:00 GMT',
          venue: 'Selhurst Park',
          homeTeamStats: {
            position: 14,
            wins: 3,
            draws: 4,
            losses: 4,
            goalsFor: 13,
            goalsAgainst: 16,
            form: ['L', 'D', 'W', 'D', 'L']
          },
          awayTeamStats: {
            position: 8,
            wins: 5,
            draws: 3,
            losses: 3,
            goalsFor: 16,
            goalsAgainst: 14,
            form: ['W', 'L', 'D', 'W', 'L']
          }
        },
        {
          id: 8,
          homeTeam: 'Everton',
          awayTeam: 'Fulham',
          gameWeek: 11,
          date: 'Saturday, 8th November 2025',
          time: '15:00 GMT',
          venue: 'Goodison Park',
          homeTeamStats: {
            position: 16,
            wins: 2,
            draws: 4,
            losses: 5,
            goalsFor: 10,
            goalsAgainst: 17,
            form: ['L', 'D', 'L', 'W', 'D']
          },
          awayTeamStats: {
            position: 12,
            wins: 4,
            draws: 3,
            losses: 4,
            goalsFor: 14,
            goalsAgainst: 15,
            form: ['W', 'L', 'D', 'L', 'W']
          },
          actualResult: {
            finalScore: '0-2',
            winner: 'away',
            matchEvents: [
              'Everton struggled to create chances at home',
              'Fulham\'s organized defense frustrated home side',
              'Two goals in second half sealed comfortable away win',
              'Everton\'s poor home form continues'
            ]
          }
        },
        {
          id: 9,
          homeTeam: 'West Ham United',
          awayTeam: 'Burnley',
          gameWeek: 11,
          date: 'Saturday, 8th November 2025',
          time: '15:00 GMT',
          venue: 'London Stadium',
          homeTeamStats: {
            position: 13,
            wins: 3,
            draws: 4,
            losses: 4,
            goalsFor: 12,
            goalsAgainst: 16,
            form: ['D', 'L', 'D', 'W', 'L']
          },
          awayTeamStats: {
            position: 19,
            wins: 1,
            draws: 3,
            losses: 7,
            goalsFor: 8,
            goalsAgainst: 23,
            form: ['L', 'L', 'D', 'L', 'W']
          }
        },
        {
          id: 10,
          homeTeam: 'Nottingham Forest',
          awayTeam: 'Leeds United',
          gameWeek: 11,
          date: 'Sunday, 9th November 2025',
          time: '14:00 GMT',
          venue: 'City Ground',
          homeTeamStats: {
            position: 17,
            wins: 2,
            draws: 4,
            losses: 5,
            goalsFor: 11,
            goalsAgainst: 19,
            form: ['L', 'D', 'W', 'L', 'D']
          },
          awayTeamStats: {
            position: 20,
            wins: 1,
            draws: 2,
            losses: 8,
            goalsFor: 7,
            goalsAgainst: 24,
            form: ['L', 'L', 'L', 'D', 'W']
          }
        }
      ];
    } else if (gameweek === 12) {
      // Gameweek 12 fixtures (15-16 November 2025)
      return [
        {
          id: 11,
          homeTeam: 'Liverpool',
          awayTeam: 'Aston Villa',
          gameWeek: 12,
          date: 'Saturday, 15th November 2025',
          time: '12:30 GMT',
          venue: 'Anfield',
          homeTeamStats: {
            position: 3,
            wins: 7,
            draws: 3,
            losses: 1,
            goalsFor: 23,
            goalsAgainst: 12,
            form: ['W', 'D', 'W', 'L', 'W']
          },
          awayTeamStats: {
            position: 9,
            wins: 5,
            draws: 2,
            losses: 4,
            goalsFor: 15,
            goalsAgainst: 16,
            form: ['W', 'L', 'D', 'W', 'L']
          }
        },
        {
          id: 12,
          homeTeam: 'Arsenal',
          awayTeam: 'Nottingham Forest',
          gameWeek: 12,
          date: 'Saturday, 15th November 2025',
          time: '15:00 GMT',
          venue: 'Emirates Stadium',
          homeTeamStats: {
            position: 2,
            wins: 8,
            draws: 3,
            losses: 0,
            goalsFor: 25,
            goalsAgainst: 10,
            form: ['D', 'W', 'W', 'D', 'W']
          },
          awayTeamStats: {
            position: 17,
            wins: 2,
            draws: 4,
            losses: 5,
            goalsFor: 11,
            goalsAgainst: 19,
            form: ['D', 'W', 'L', 'D', 'L']
          }
        },
        {
          id: 13,
          homeTeam: 'Manchester United',
          awayTeam: 'Leeds United',
          gameWeek: 12,
          date: 'Saturday, 15th November 2025',
          time: '15:00 GMT',
          venue: 'Old Trafford',
          homeTeamStats: {
            position: 6,
            wins: 6,
            draws: 2,
            losses: 3,
            goalsFor: 18,
            goalsAgainst: 15,
            form: ['L', 'W', 'D', 'L', 'W']
          },
          awayTeamStats: {
            position: 20,
            wins: 1,
            draws: 2,
            losses: 8,
            goalsFor: 7,
            goalsAgainst: 24,
            form: ['L', 'L', 'D', 'W', 'L']
          }
        },
        {
          id: 14,
          homeTeam: 'Brighton & Hove Albion',
          awayTeam: 'Manchester City',
          gameWeek: 12,
          date: 'Saturday, 15th November 2025',
          time: '17:30 GMT',
          venue: 'Amex Stadium',
          homeTeamStats: {
            position: 8,
            wins: 5,
            draws: 3,
            losses: 3,
            goalsFor: 16,
            goalsAgainst: 14,
            form: ['L', 'D', 'W', 'L', 'W']
          },
          awayTeamStats: {
            position: 1,
            wins: 9,
            draws: 2,
            losses: 0,
            goalsFor: 28,
            goalsAgainst: 8,
            form: ['W', 'D', 'W', 'W', 'W']
          }
        },
        {
          id: 15,
          homeTeam: 'Wolverhampton Wanderers',
          awayTeam: 'Fulham',
          gameWeek: 12,
          date: 'Saturday, 15th November 2025',
          time: '15:00 GMT',
          venue: 'Molineux Stadium',
          homeTeamStats: {
            position: 18,
            wins: 2,
            draws: 3,
            losses: 6,
            goalsFor: 11,
            goalsAgainst: 22,
            form: ['L', 'D', 'W', 'L', 'L']
          },
          awayTeamStats: {
            position: 12,
            wins: 4,
            draws: 3,
            losses: 4,
            goalsFor: 14,
            goalsAgainst: 15,
            form: ['L', 'D', 'L', 'W', 'W']
          }
        },
        {
          id: 16,
          homeTeam: 'Newcastle United',
          awayTeam: 'West Ham United',
          gameWeek: 12,
          date: 'Sunday, 16th November 2025',
          time: '14:00 GMT',
          venue: 'St. James\' Park',
          homeTeamStats: {
            position: 7,
            wins: 5,
            draws: 4,
            losses: 2,
            goalsFor: 17,
            goalsAgainst: 12,
            form: ['W', 'D', 'W', 'D', 'D']
          },
          awayTeamStats: {
            position: 13,
            wins: 3,
            draws: 4,
            losses: 4,
            goalsFor: 12,
            goalsAgainst: 16,
            form: ['D', 'L', 'W', 'L', 'D']
          }
        },
        {
          id: 17,
          homeTeam: 'AFC Bournemouth',
          awayTeam: 'Everton',
          gameWeek: 12,
          date: 'Sunday, 16th November 2025',
          time: '14:00 GMT',
          venue: 'Vitality Stadium',
          homeTeamStats: {
            position: 11,
            wins: 4,
            draws: 3,
            losses: 4,
            goalsFor: 14,
            goalsAgainst: 15,
            form: ['L', 'D', 'W', 'L', 'W']
          },
          awayTeamStats: {
            position: 16,
            wins: 2,
            draws: 4,
            losses: 5,
            goalsFor: 10,
            goalsAgainst: 17,
            form: ['D', 'L', 'W', 'D', 'L']
          }
        },
        {
          id: 18,
          homeTeam: 'Burnley',
          awayTeam: 'Crystal Palace',
          gameWeek: 12,
          date: 'Sunday, 16th November 2025',
          time: '14:00 GMT',
          venue: 'Turf Moor',
          homeTeamStats: {
            position: 19,
            wins: 1,
            draws: 3,
            losses: 7,
            goalsFor: 8,
            goalsAgainst: 23,
            form: ['L', 'D', 'L', 'W', 'L']
          },
          awayTeamStats: {
            position: 14,
            wins: 3,
            draws: 4,
            losses: 4,
            goalsFor: 13,
            goalsAgainst: 16,
            form: ['D', 'W', 'D', 'L', 'L']
          }
        },
        {
          id: 19,
          homeTeam: 'Chelsea',
          awayTeam: 'Brentford',
          gameWeek: 12,
          date: 'Sunday, 16th November 2025',
          time: '16:30 GMT',
          venue: 'Stamford Bridge',
          homeTeamStats: {
            position: 5,
            wins: 6,
            draws: 3,
            losses: 2,
            goalsFor: 21,
            goalsAgainst: 14,
            form: ['D', 'W', 'L', 'W', 'W']
          },
          awayTeamStats: {
            position: 10,
            wins: 4,
            draws: 4,
            losses: 3,
            goalsFor: 16,
            goalsAgainst: 15,
            form: ['W', 'D', 'L', 'W', 'D']
          }
        },
        {
          id: 20,
          homeTeam: 'Sunderland',
          awayTeam: 'Tottenham Hotspur',
          gameWeek: 12,
          date: 'Sunday, 16th November 2025',
          time: '19:00 GMT',
          venue: 'Stadium of Light',
          homeTeamStats: {
            position: 15,
            wins: 3,
            draws: 3,
            losses: 5,
            goalsFor: 12,
            goalsAgainst: 18,
            form: ['D', 'W', 'L', 'D', 'L']
          },
          awayTeamStats: {
            position: 4,
            wins: 7,
            draws: 2,
            losses: 2,
            goalsFor: 22,
            goalsAgainst: 13,
            form: ['W', 'D', 'L', 'W', 'W']
          }
        }
      ];
    }
    return [];
  };

  // Load Premier League matches when sportsbook tab is active or gameweek changes
  useEffect(() => {
    if (activeTab === 'sportsbook') {
      const fixtures = getFixturesForGameweek(currentGameweek);
      setPremierLeagueMatches(fixtures);
    }
  }, [activeTab, currentGameweek]);

  // AI Prediction Algorithm based on team stats and form
  const generatePrediction = (match: PremierLeagueMatch): PremierLeagueMatch => {
    const { homeTeamStats, awayTeamStats } = match;

    // Calculate form score (last 5 games)
    const calculateFormScore = (form: string[]) => {
      return form.reduce((score, result) => {
        if (result === 'W') return score + 3;
        if (result === 'D') return score + 1;
        return score;
      }, 0);
    };

    const homeFormScore = calculateFormScore(homeTeamStats.form);
    const awayFormScore = calculateFormScore(awayTeamStats.form);

    // Calculate goal difference
    const homeGoalDiff = homeTeamStats.goalsFor - homeTeamStats.goalsAgainst;
    const awayGoalDiff = awayTeamStats.goalsFor - awayTeamStats.goalsAgainst;

    // Calculate points per game
    const homeTotalGames = homeTeamStats.wins + homeTeamStats.draws + homeTeamStats.losses;
    const awayTotalGames = awayTeamStats.wins + awayTeamStats.draws + awayTeamStats.losses;
    const homePoints = (homeTeamStats.wins * 3) + homeTeamStats.draws;
    const awayPoints = (awayTeamStats.wins * 3) + awayTeamStats.draws;
    const homePointsPerGame = homePoints / homeTotalGames;
    const awayPointsPerGame = awayPoints / awayTotalGames;

    // Home advantage factor (typically 10-15% advantage)
    const homeAdvantage = 0.12;

    // Weighted scoring system
    const homeScore = 
      (homePointsPerGame * 30) +
      (homeFormScore * 3) +
      (homeGoalDiff * 2) +
      ((30 - homeTeamStats.position) * 1.5) +
      (homeAdvantage * 100);

    const awayScore = 
      (awayPointsPerGame * 30) +
      (awayFormScore * 3) +
      (awayGoalDiff * 2) +
      ((30 - awayTeamStats.position) * 1.5);

    // Calculate probabilities
    const totalScore = homeScore + awayScore;
    let homeWinProb = (homeScore / totalScore) * 100;
    let awayWinProb = (awayScore / totalScore) * 100;

    // Adjust for draw probability (typically 25-30% in football)
    const drawProbBase = 26;
    const drawProb = drawProbBase + (Math.abs(homeScore - awayScore) < 5 ? 5 : 0);

    // Normalize probabilities
    const remaining = 100 - drawProb;
    homeWinProb = (homeWinProb / (homeWinProb + awayWinProb)) * remaining;
    awayWinProb = (awayWinProb / (homeWinProb + awayWinProb)) * remaining;

    // Determine most likely outcome
    const probabilities = { home: homeWinProb, draw: drawProb, away: awayWinProb };
    const mostLikely = Object.keys(probabilities).reduce((a, b) => 
      probabilities[a as keyof typeof probabilities] > probabilities[b as keyof typeof probabilities] ? a : b
    ) as 'home' | 'draw' | 'away';

    // Calculate confidence (higher spread = higher confidence)
    const spread = Math.max(homeWinProb, drawProb, awayWinProb) - Math.min(homeWinProb, drawProb, awayWinProb);
    const confidence = Math.min(95, 50 + (spread * 0.8));

    // Predict score based on average goals
    const homeExpectedGoals = Math.round((homeTeamStats.goalsFor / homeTotalGames) * 0.9);
    const awayExpectedGoals = Math.round((awayTeamStats.goalsFor / awayTotalGames) * 0.7); // Away teams typically score less

    let predictedScore = `${homeExpectedGoals}-${awayExpectedGoals}`;
    if (mostLikely === 'draw') {
      const avgGoals = Math.round((homeExpectedGoals + awayExpectedGoals) / 2);
      predictedScore = `${avgGoals}-${avgGoals}`;
    } else if (mostLikely === 'away') {
      predictedScore = `${Math.max(0, awayExpectedGoals - 1)}-${awayExpectedGoals + 1}`;
    }

    return {
      ...match,
      prediction: {
        homeWin: Math.round(homeWinProb * 10) / 10,
        draw: Math.round(drawProb * 10) / 10,
        awayWin: Math.round(awayWinProb * 10) / 10,
        mostLikely,
        confidence: Math.round(confidence * 10) / 10,
        predictedScore
      }
    };
  };

  const handleGeneratePrediction = (matchId: number) => {
    setPremierLeagueMatches(prevMatches =>
      prevMatches.map(match =>
        match.id === matchId ? generatePrediction(match) : match
      )
    );
  };

  // Save prediction to database
  const handleSavePrediction = async (match: PremierLeagueMatch) => {
    if (!match.prediction) return;

    // Check if already saved (based on gameweek + teams)
    const isDuplicate = savedPredictions.some(
      saved => saved.gameWeek === match.gameWeek &&
               saved.homeTeam === match.homeTeam &&
               saved.awayTeam === match.awayTeam
    );

    if (isDuplicate) {
      alert('This prediction has already been saved!');
      return;
    }

    try {
      const saved = await igamingService.savePrediction(match as any);
      setSavedPredictions(prev => [...prev, saved as any]);
      alert('Prediction saved successfully!');
    } catch (err: any) {
      console.error('Failed to save prediction:', err);
      alert(err.message || 'Failed to save prediction. Please try again.');
    }
  };

  // Delete saved prediction
  const handleDeletePrediction = async (matchId: number, gameWeek: number) => {
    try {
      // Find the prediction with both id and gameweek
      const predictionToDelete = savedPredictions.find(
        m => m.id === matchId && m.gameWeek === gameWeek
      );

      if (!predictionToDelete || !predictionToDelete.id) {
        alert('Prediction not found');
        return;
      }

      await igamingService.deleteSavedPrediction(predictionToDelete.id);
      setSavedPredictions(prev => prev.filter(m => m.id !== predictionToDelete.id));
    } catch (err: any) {
      console.error('Failed to delete prediction:', err);
      alert(err.message || 'Failed to delete prediction. Please try again.');
    }
  };

  // Completed matches with AI predictions vs actual results (Gameweek 10)
  const completedMatchesWithPredictions: PremierLeagueMatch[] = [
    {
      id: 101,
      homeTeam: 'Arsenal',
      awayTeam: 'Liverpool',
      gameWeek: 10,
      date: 'Saturday, 1st November 2025',
      time: '17:30 GMT',
      venue: 'Emirates Stadium',
      homeTeamStats: { position: 2, wins: 8, draws: 2, losses: 0, goalsFor: 24, goalsAgainst: 9, form: ['W', 'D', 'W', 'W', 'D'] },
      awayTeamStats: { position: 3, wins: 7, draws: 2, losses: 1, goalsFor: 22, goalsAgainst: 11, form: ['W', 'D', 'W', 'L', 'W'] },
      prediction: { homeWin: 45, draw: 28, awayWin: 27, mostLikely: 'home', confidence: 72, predictedScore: '2-1' },
      actualResult: { 
        finalScore: '1-2', 
        winner: 'away',
        matchEvents: [
          'Early red card for Arsenal defender in 23rd minute',
          'Liverpool dominated possession after red card (68%)',
          'Arsenal goalkeeper made 8 crucial saves',
          'Liverpool\'s late goal in 89th minute sealed victory'
        ]
      }
    },
    {
      id: 102,
      homeTeam: 'Manchester City',
      awayTeam: 'Chelsea',
      gameWeek: 10,
      date: 'Sunday, 2nd November 2025',
      time: '16:30 GMT',
      venue: 'Etihad Stadium',
      homeTeamStats: { position: 1, wins: 9, draws: 1, losses: 0, goalsFor: 27, goalsAgainst: 7, form: ['W', 'W', 'D', 'W', 'W'] },
      awayTeamStats: { position: 5, wins: 6, draws: 2, losses: 2, goalsFor: 20, goalsAgainst: 13, form: ['D', 'W', 'L', 'W', 'W'] },
      prediction: { homeWin: 62, draw: 22, awayWin: 16, mostLikely: 'home', confidence: 85, predictedScore: '3-1' },
      actualResult: { 
        finalScore: '3-1', 
        winner: 'home',
        matchEvents: [
          'Man City scored early in 12th minute',
          'Chelsea equalized in 34th minute',
          'Man City dominated second half',
          'Two goals in final 20 minutes sealed victory'
        ]
      }
    },
    {
      id: 103,
      homeTeam: 'Tottenham Hotspur',
      awayTeam: 'Aston Villa',
      gameWeek: 10,
      date: 'Saturday, 1st November 2025',
      time: '15:00 GMT',
      venue: 'Tottenham Hotspur Stadium',
      homeTeamStats: { position: 4, wins: 7, draws: 1, losses: 2, goalsFor: 21, goalsAgainst: 12, form: ['W', 'D', 'L', 'W', 'W'] },
      awayTeamStats: { position: 9, wins: 5, draws: 1, losses: 4, goalsFor: 14, goalsAgainst: 15, form: ['W', 'L', 'W', 'L', 'D'] },
      prediction: { homeWin: 58, draw: 24, awayWin: 18, mostLikely: 'home', confidence: 78, predictedScore: '2-0' },
      actualResult: { 
        finalScore: '2-2', 
        winner: 'draw',
        matchEvents: [
          'Tottenham took 2-0 lead by 35th minute',
          'Aston Villa\'s tactical substitution changed the game',
          'Villa scored twice in 10-minute spell (68\', 78\')',
          'Tottenham failed to convert 3 clear chances in final minutes'
        ]
      }
    },
    {
      id: 104,
      homeTeam: 'Manchester United',
      awayTeam: 'Newcastle United',
      gameWeek: 10,
      date: 'Saturday, 1st November 2025',
      time: '15:00 GMT',
      venue: 'Old Trafford',
      homeTeamStats: { position: 6, wins: 6, draws: 1, losses: 3, goalsFor: 17, goalsAgainst: 14, form: ['L', 'W', 'D', 'L', 'W'] },
      awayTeamStats: { position: 7, wins: 5, draws: 3, losses: 2, goalsFor: 16, goalsAgainst: 11, form: ['D', 'W', 'D', 'W', 'D'] },
      prediction: { homeWin: 42, draw: 30, awayWin: 28, mostLikely: 'home', confidence: 64, predictedScore: '1-1' },
      actualResult: { 
        finalScore: '1-1', 
        winner: 'draw',
        matchEvents: [
          'Even match with balanced possession (51%-49%)',
          'Newcastle took lead in 42nd minute',
          'Man United equalized from penalty in 67th minute',
          'Both teams had chances to win but settled for draw'
        ]
      }
    },
    {
      id: 105,
      homeTeam: 'Brighton & Hove Albion',
      awayTeam: 'Burnley',
      gameWeek: 10,
      date: 'Sunday, 2nd November 2025',
      time: '14:00 GMT',
      venue: 'Amex Stadium',
      homeTeamStats: { position: 8, wins: 5, draws: 2, losses: 3, goalsFor: 15, goalsAgainst: 13, form: ['L', 'D', 'W', 'L', 'W'] },
      awayTeamStats: { position: 19, wins: 1, draws: 2, losses: 7, goalsFor: 7, goalsAgainst: 22, form: ['L', 'D', 'L', 'W', 'L'] },
      prediction: { homeWin: 68, draw: 20, awayWin: 12, mostLikely: 'home', confidence: 82, predictedScore: '2-0' },
      actualResult: { 
        finalScore: '0-1', 
        winner: 'away',
        matchEvents: [
          'Brighton dominated but lacked clinical finishing (22 shots, 4 on target)',
          'Burnley\'s defensive masterclass with 11 men behind ball',
          'Counter-attack goal in 73rd minute against run of play',
          'Brighton\'s star striker missed penalty in 85th minute'
        ]
      }
    }
  ];

  // Analyze why prediction failed
  const analyzePredictionFailure = (match: PremierLeagueMatch): string => {
    if (!match.prediction || !match.actualResult) return '';
    
    const predicted = match.prediction.mostLikely;
    const actual = match.actualResult.winner;
    
    if (predicted === actual) return '';

    const events = match.actualResult.matchEvents;
    let analysis = '';

    // Check for red card
    if (events.some(e => e.toLowerCase().includes('red card'))) {
      analysis += '**Red Card Impact**: An early sending-off dramatically altered the match dynamics. Our AI model could not predict disciplinary actions, which significantly changed the tactical balance and team\'s ability to execute their game plan. Playing with 10 men reduced offensive capabilities and forced defensive reshuffling.\n\n';
    }

    // Check for missed penalty
    if (events.some(e => e.toLowerCase().includes('missed penalty'))) {
      analysis += '**Missed Penalty Opportunity**: A critical penalty miss prevented the expected goal that would have changed the match outcome. Penalty conversion rates are factored into our model at 75-80%, but individual execution on the day cannot be predicted with certainty.\n\n';
    }

    // Check for tactical changes
    if (events.some(e => e.toLowerCase().includes('tactical') || e.toLowerCase().includes('substitution'))) {
      analysis += '**Tactical Adjustments**: Mid-game tactical changes by the manager proved decisive. Our AI analyzes pre-match formations and strategies, but cannot predict in-game tactical switches, player substitutions, or formation changes that dramatically shift momentum.\n\n';
    }

    // Check for late goals
    if (events.some(e => e.includes('89th') || e.includes('late goal') || e.includes('final minutes'))) {
      analysis += '**Late Game Momentum Shift**: Goals scored in the dying minutes of the match changed the final result. Our model weighs expected goals throughout 90 minutes, but cannot account for psychological factors, fatigue, or desperation tactics in injury time.\n\n';
    }

    // Check for defensive performance
    if (events.some(e => e.toLowerCase().includes('saves') || e.toLowerCase().includes('defensive masterclass'))) {
      analysis += '**Exceptional Individual Performance**: An outstanding individual performance (goalkeeper saves or defensive display) exceeded statistical expectations. While we factor in team defensive records, exceptional individual performances on the day fall outside normal statistical ranges.\n\n';
    }

    // Check for poor finishing
    if (events.some(e => e.toLowerCase().includes('failed to convert') || e.toLowerCase().includes('lacked clinical finishing') || e.includes('shots'))) {
      analysis += '**Clinical Finishing Variance**: Despite dominating possession and chances created, poor conversion rates led to an unexpected result. Expected goals (xG) suggested a different outcome, but actual finishing quality on the day significantly underperformed statistical expectations.\n\n';
    }

    // Check for counter-attack
    if (events.some(e => e.toLowerCase().includes('counter-attack') || e.toLowerCase().includes('against run of play'))) {
      analysis += '**Counter-Attack Success**: A goal scored against the run of play from a counter-attack defied possession and territorial dominance statistics. Our model weights possession and attacking metrics heavily, but cannot fully account for the effectiveness of counter-attacking strategies.\n\n';
    }

    // Add confidence-based analysis
    if (match.prediction.confidence < 70) {
      analysis += `**Low Confidence Prediction**: Our AI had only ${match.prediction.confidence}% confidence in this prediction due to closely matched team statistics and recent form. In such evenly-matched fixtures, marginal factors and individual moments of quality often decide outcomes, making accurate predictions statistically challenging.\n\n`;
    }

    // General analysis based on score difference
    const [homeGoals, awayGoals] = match.actualResult.finalScore.split('-').map(Number);
    const goalDifference = Math.abs(homeGoals - awayGoals);
    
    if (goalDifference > 2) {
      analysis += '**Unexpected Goal Margin**: The final score showed a more decisive victory than predicted. This suggests our model underestimated the winning team\'s attacking effectiveness or overestimated the losing team\'s defensive capabilities on this specific match day.\n\n';
    }

    if (!analysis) {
      analysis = '**Statistical Variance**: Football matches contain inherent unpredictability. While our AI model analyzes comprehensive statistics including form, head-to-head records, and team strengths, individual match moments, player execution, and psychological factors can produce outcomes outside statistical probabilities. This match demonstrated why football remains the "beautiful unpredictable game."\n\n';
    }

    return analysis.trim();
  };

  // Generate head-to-head data and analysis
  const generateHeadToHead = (match: PremierLeagueMatch) => {
    // Mock head-to-head history (last 5 meetings)
    const headToHeadMatches = [
      { date: 'Apr 2025', homeTeam: match.homeTeam, awayTeam: match.awayTeam, score: '2-1', winner: 'home' },
      { date: 'Nov 2024', homeTeam: match.awayTeam, awayTeam: match.homeTeam, score: '1-1', winner: 'draw' },
      { date: 'Mar 2024', homeTeam: match.homeTeam, awayTeam: match.awayTeam, score: '3-2', winner: 'home' },
      { date: 'Oct 2023', homeTeam: match.awayTeam, awayTeam: match.homeTeam, score: '2-0', winner: 'away' },
      { date: 'Apr 2023', homeTeam: match.homeTeam, awayTeam: match.awayTeam, score: '1-1', winner: 'draw' }
    ];

    // Calculate head-to-head stats
    const homeWins = headToHeadMatches.filter(m => 
      (m.homeTeam === match.homeTeam && m.winner === 'home') || 
      (m.homeTeam === match.awayTeam && m.winner === 'away')
    ).length;
    const awayWins = headToHeadMatches.filter(m => 
      (m.homeTeam === match.homeTeam && m.winner === 'away') || 
      (m.homeTeam === match.awayTeam && m.winner === 'home')
    ).length;
    const draws = headToHeadMatches.filter(m => m.winner === 'draw').length;

    // Generate key insights
    const insights = [];
    
    // Form analysis
    const homeFormScore = match.homeTeamStats.form.filter(r => r === 'W').length;
    const awayFormScore = match.awayTeamStats.form.filter(r => r === 'W').length;
    if (homeFormScore > awayFormScore) {
      insights.push(`${match.homeTeam} are in better form with ${homeFormScore} wins in their last 5 matches`);
    } else if (awayFormScore > homeFormScore) {
      insights.push(`${match.awayTeam} are in better form with ${awayFormScore} wins in their last 5 matches`);
    } else {
      insights.push(`Both teams have similar recent form with ${homeFormScore} wins each`);
    }

    // Goal difference analysis
    const homeGoalDiff = match.homeTeamStats.goalsFor - match.homeTeamStats.goalsAgainst;
    const awayGoalDiff = match.awayTeamStats.goalsFor - match.awayTeamStats.goalsAgainst;
    if (homeGoalDiff > awayGoalDiff + 5) {
      insights.push(`${match.homeTeam} have a significantly better goal difference (+${homeGoalDiff})`);
    } else if (awayGoalDiff > homeGoalDiff + 5) {
      insights.push(`${match.awayTeam} have a significantly better goal difference (+${awayGoalDiff})`);
    }

    // Attacking strength
    if (match.homeTeamStats.goalsFor > match.awayTeamStats.goalsFor) {
      insights.push(`${match.homeTeam} have scored more goals this season (${match.homeTeamStats.goalsFor} vs ${match.awayTeamStats.goalsFor})`);
    } else if (match.awayTeamStats.goalsFor > match.homeTeamStats.goalsFor) {
      insights.push(`${match.awayTeam} have scored more goals this season (${match.awayTeamStats.goalsFor} vs ${match.homeTeamStats.goalsFor})`);
    }

    // Defensive strength
    if (match.homeTeamStats.goalsAgainst < match.awayTeamStats.goalsAgainst) {
      insights.push(`${match.homeTeam} have a stronger defense, conceding only ${match.homeTeamStats.goalsAgainst} goals`);
    } else if (match.awayTeamStats.goalsAgainst < match.homeTeamStats.goalsAgainst) {
      insights.push(`${match.awayTeam} have a stronger defense, conceding only ${match.awayTeamStats.goalsAgainst} goals`);
    }

    // Head-to-head dominance
    if (homeWins > awayWins) {
      insights.push(`${match.homeTeam} dominate the head-to-head record (${homeWins} wins vs ${awayWins})`);
    } else if (awayWins > homeWins) {
      insights.push(`${match.awayTeam} dominate the head-to-head record (${awayWins} wins vs ${homeWins})`);
    } else {
      insights.push(`The head-to-head record is evenly balanced (${homeWins}-${draws}-${awayWins})`);
    }

    return {
      headToHeadMatches,
      homeWins,
      awayWins,
      draws,
      insights
    };
  };

  const tabs = [
    { id: 'casino' as const, label: 'Casino', icon: '🎰' },
    { id: 'sportsbook' as const, label: 'Sportsbook', icon: '⚽' },
    { id: 'live' as const, label: 'Live Casino', icon: '📺' }
  ];

  const filteredCasinoGames = casinoGames.filter(game =>
    !searchQuery || 
    game.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    game.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
    game.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handlePlayGame = async (gameId: number) => {
    try {
      setLoading(true);
      // Navigate to game player page
      navigate(`/game/${gameId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to launch game');
      setLoading(false);
    }
  };

  const GameCard = ({ game }: { game: Game }) => (
    <div style={{
      background: '#ffffff',
      borderRadius: 16,
      padding: isMobile ? 16 : 20,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '1px solid #e5e7eb',
      maxWidth: '100%',
      overflow: 'hidden'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.12)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
    }}
    >
      <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 12 }}>{game.image}</div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', margin: '0 0 8px 0' }}>{game.name}</h3>
      <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>
        <div>Provider: {game.provider}</div>
        <div>Category: {game.category}</div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <span style={{
          padding: '4px 8px',
          background: '#e0f2fe',
          color: '#0369a1',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600
        }}>
          RTP: {game.rtp}
        </span>
        <span style={{
          padding: '4px 8px',
          background: game.volatility === 'High' ? '#fef3c7' : '#dcfce7',
          color: game.volatility === 'High' ? '#92400e' : '#166534',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600
        }}>
          {game.volatility}
        </span>
      </div>
      <button 
        onClick={() => handlePlayGame(game.id)}
        disabled={loading}
        style={{
          width: '100%',
          padding: '10px',
          background: branding.primaryColor,
          color: '#ffffff',
          border: 'none',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.6 : 1
        }}
      >
        {loading ? 'Loading...' : 'Play Now'}
      </button>
    </div>
  );

  const SportsEventCard = ({ event }: { event: SportsEvent }) => (
    <div style={{
      background: '#ffffff',
      borderRadius: 16,
      padding: 20,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb',
      marginBottom: 16
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>
            {event.sport} • {event.league}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
            {event.date} • {event.time}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>{event.homeTeam}</div>
        </div>
        <div style={{ padding: '0 16px', fontSize: 18, color: '#6b7280', fontWeight: 600 }}>vs</div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>{event.awayTeam}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={{
          flex: 1,
          padding: '8px',
          background: '#f3f4f6',
          color: '#1f2937',
          border: 'none',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer'
        }}>
          View Odds
        </button>
        <button style={{
          flex: 1,
          padding: '8px',
          background: branding.primaryColor,
          color: '#ffffff',
          border: 'none',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer'
        }}>
          Place Bet
        </button>
      </div>
    </div>
  );

  // Premier League Match Card with AI Prediction
  const PremierLeagueMatchCard = ({ match }: { match: PremierLeagueMatch }) => {
    const getFormIcon = (result: string) => {
      if (result === 'W') return <span style={{ color: '#10b981', fontWeight: 700 }}>W</span>;
      if (result === 'D') return <span style={{ color: '#f59e0b', fontWeight: 700 }}>D</span>;
      return <span style={{ color: '#ef4444', fontWeight: 700 }}>L</span>;
    };

    return (
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        padding: isMobile ? '16px' : '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        border: '1px solid #e5e7eb',
        marginBottom: 20,
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        {/* Match Header */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase' }}>
                ⚽ Premier League
              </span>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 700
            }}>
              Gameweek {match.gameWeek}
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
            📅 {match.date}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            🕐 {match.time} • 📍 {match.venue}
          </div>
        </div>

        {/* Teams Section */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: isMobile ? 8 : 16, marginBottom: 20 }}>
          {/* Home Team */}
          <div style={{ textAlign: 'center', minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, color: '#1f2937', marginBottom: 8, wordWrap: 'break-word' }}>
              {match.homeTeam}
            </div>
            <div style={{ fontSize: isMobile ? 9 : 11, color: '#6b7280', marginBottom: 4 }}>
              #{match.homeTeamStats.position} | GD: {match.homeTeamStats.goalsFor - match.homeTeamStats.goalsAgainst}
            </div>
            <div style={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: 6, flexWrap: 'wrap' }}>
              {match.homeTeamStats.form.map((result, idx) => (
                <div key={idx} style={{
                  padding: isMobile ? '1px 4px' : '2px 6px',
                  background: result === 'W' ? '#d1fae5' : result === 'D' ? '#fef3c7' : '#fee2e2',
                  borderRadius: 4,
                  fontSize: isMobile ? 8 : 10,
                  fontWeight: 600
                }}>
                  {getFormIcon(result)}
                </div>
              ))}
            </div>
          </div>

          {/* VS Divider */}
          <div style={{ display: 'flex', alignItems: 'center', padding: isMobile ? '0 4px' : '0 12px' }}>
            <span style={{ fontSize: isMobile ? 14 : 20, fontWeight: 700, color: '#6b7280' }}>VS</span>
          </div>

          {/* Away Team */}
          <div style={{ textAlign: 'center', minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, color: '#1f2937', marginBottom: 8, wordWrap: 'break-word' }}>
              {match.awayTeam}
            </div>
            <div style={{ fontSize: isMobile ? 9 : 11, color: '#6b7280', marginBottom: 4 }}>
              #{match.awayTeamStats.position} | GD: {match.awayTeamStats.goalsFor - match.awayTeamStats.goalsAgainst}
            </div>
            <div style={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: 6, flexWrap: 'wrap' }}>
              {match.awayTeamStats.form.map((result, idx) => (
                <div key={idx} style={{
                  padding: isMobile ? '1px 4px' : '2px 6px',
                  background: result === 'W' ? '#d1fae5' : result === 'D' ? '#fef3c7' : '#fee2e2',
                  borderRadius: 4,
                  fontSize: isMobile ? 8 : 10,
                  fontWeight: 600
                }}>
                  {getFormIcon(result)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Prediction Section */}
        {match.prediction ? (
          <div style={{
            background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
            padding: isMobile ? 12 : 16,
            borderRadius: 12,
            marginBottom: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <FaBrain style={{ color: '#8b5cf6', fontSize: 18 }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: '#6b21a8' }}>
                AI Prediction
              </span>
              <span style={{
                marginLeft: 'auto',
                fontSize: 11,
                fontWeight: 600,
                color: '#7c3aed',
                background: '#fff',
                padding: '4px 8px',
                borderRadius: 6
              }}>
                {match.prediction.confidence}% Confidence
              </span>
            </div>

            {/* Predicted Score */}
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#7c3aed', marginBottom: 4 }}>Most Likely Score</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#6b21a8' }}>
                {match.prediction.predictedScore}
              </div>
            </div>

            {/* Probabilities */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: isMobile ? 4 : 8 }}>
              <div style={{
                background: match.prediction.mostLikely === 'home' ? '#10b981' : '#fff',
                padding: isMobile ? '8px 4px' : '10px',
                borderRadius: 8,
                textAlign: 'center',
                border: match.prediction.mostLikely === 'home' ? '2px solid #059669' : '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: isMobile ? 9 : 10,
                  fontWeight: 600,
                  color: match.prediction.mostLikely === 'home' ? '#fff' : '#6b7280',
                  marginBottom: 4
                }}>
                  Home Win
                </div>
                <div style={{
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 700,
                  color: match.prediction.mostLikely === 'home' ? '#fff' : '#1f2937'
                }}>
                  {match.prediction.homeWin}%
                </div>
              </div>

              <div style={{
                background: match.prediction.mostLikely === 'draw' ? '#f59e0b' : '#fff',
                padding: isMobile ? '8px 4px' : '10px',
                borderRadius: 8,
                textAlign: 'center',
                border: match.prediction.mostLikely === 'draw' ? '2px solid #d97706' : '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: isMobile ? 9 : 10,
                  fontWeight: 600,
                  color: match.prediction.mostLikely === 'draw' ? '#fff' : '#6b7280',
                  marginBottom: 4
                }}>
                  Draw
                </div>
                <div style={{
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 700,
                  color: match.prediction.mostLikely === 'draw' ? '#fff' : '#1f2937'
                }}>
                  {match.prediction.draw}%
                </div>
              </div>

              <div style={{
                background: match.prediction.mostLikely === 'away' ? '#ef4444' : '#fff',
                padding: isMobile ? '8px 4px' : '10px',
                borderRadius: 8,
                textAlign: 'center',
                border: match.prediction.mostLikely === 'away' ? '2px solid #dc2626' : '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: isMobile ? 9 : 10,
                  fontWeight: 600,
                  color: match.prediction.mostLikely === 'away' ? '#fff' : '#6b7280',
                  marginBottom: 4
                }}>
                  Away Win
                </div>
                <div style={{
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 700,
                  color: match.prediction.mostLikely === 'away' ? '#fff' : '#1f2937'
                }}>
                  {match.prediction.awayWin}%
                </div>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => handleGeneratePrediction(match.id)}
            style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
            }}
          >
            <FaBrain /> Generate AI Prediction
          </button>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: match.prediction ? '1fr 1fr 1fr' : '1fr 1fr', gap: 8, marginTop: 12 }}>
          <button
            onClick={() => setSelectedMatchForStats(match)}
            style={{
              padding: isMobile ? '8px' : '10px',
              background: '#f3f4f6',
              color: '#1f2937',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              fontSize: isMobile ? 11 : 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6
            }}
          >
            <FaChartLine /> {isMobile ? 'Stats' : 'View Stats'}
          </button>
          {match.prediction && (
            <button
              onClick={() => handleSavePrediction(match)}
              style={{
                padding: isMobile ? '8px' : '10px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 8,
                fontSize: isMobile ? 11 : 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6
              }}
            >
              <FaBookmark /> {isMobile ? 'Save' : 'Save'}
            </button>
          )}
          <button style={{
            padding: isMobile ? '8px' : '10px',
            background: branding.primaryColor,
            color: '#ffffff',
            border: 'none',
            borderRadius: 8,
            fontSize: isMobile ? 11 : 13,
            fontWeight: 600,
            cursor: 'pointer'
          }}>
            Place Bet
          </button>
        </div>
      </div>
    );
  };

  // Stats Modal Component
  const StatsModal = ({ match }: { match: PremierLeagueMatch }) => {
    const h2hData = generateHeadToHead(match);

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '16px' : '24px',
        overflowY: 'auto'
      }}
      onClick={() => setSelectedMatchForStats(null)}
      >
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          maxWidth: 900,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: isMobile ? '20px' : '32px',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={() => setSelectedMatchForStats(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 18,
              fontWeight: 700
            }}
          >
            ×
          </button>

          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
              Match Analysis & Statistics
            </h2>
            <div style={{ fontSize: isMobile ? 14 : 16, color: '#6b7280', fontWeight: 600 }}>
              {match.homeTeam} vs {match.awayTeam}
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              {match.date} • {match.time} • {match.venue}
            </div>
          </div>

          {/* Head-to-Head Record */}
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>🏆</span> Head-to-Head Record (Last 5 Meetings)
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                padding: '16px',
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#d1fae5', marginBottom: 4 }}>{match.homeTeam} Wins</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#ffffff' }}>{h2hData.homeWins}</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                padding: '16px',
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#fef3c7', marginBottom: 4 }}>Draws</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#ffffff' }}>{h2hData.draws}</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                padding: '16px',
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#dbeafe', marginBottom: 4 }}>{match.awayTeam} Wins</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#ffffff' }}>{h2hData.awayWins}</div>
              </div>
            </div>

            {/* Recent Matches */}
            <div style={{ background: '#f9fafb', borderRadius: 12, padding: isMobile ? '12px' : '16px' }}>
              {h2hData.headToHeadMatches.map((h2hMatch, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: idx < h2hData.headToHeadMatches.length - 1 ? '1px solid #e5e7eb' : 'none'
                }}>
                  <div style={{ fontSize: isMobile ? 11 : 12, color: '#6b7280', fontWeight: 600, minWidth: 60 }}>{h2hMatch.date}</div>
                  <div style={{ flex: 1, fontSize: isMobile ? 12 : 13, color: '#1f2937', fontWeight: 600, textAlign: 'center' }}>
                    {h2hMatch.homeTeam} vs {h2hMatch.awayTeam}
                  </div>
                  <div style={{
                    padding: '4px 12px',
                    background: h2hMatch.winner === 'home' ? '#d1fae5' : h2hMatch.winner === 'away' ? '#dbeafe' : '#fef3c7',
                    color: h2hMatch.winner === 'home' ? '#065f46' : h2hMatch.winner === 'away' ? '#1e40af' : '#92400e',
                    borderRadius: 6,
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: 700,
                    minWidth: 50,
                    textAlign: 'center'
                  }}>
                    {h2hMatch.score}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Team Comparison */}
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>📊</span> Season Statistics Comparison
            </h3>
            <div style={{ display: 'grid', gap: 12 }}>
              {/* League Position */}
              <div style={{ background: '#f9fafb', borderRadius: 12, padding: '16px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>League Position</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1f2937' }}>#{match.homeTeamStats.position}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>vs</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1f2937' }}>#{match.awayTeamStats.position}</div>
                </div>
              </div>

              {/* Goals Scored */}
              <div style={{ background: '#f9fafb', borderRadius: 12, padding: '16px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>Goals Scored</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>{match.homeTeamStats.goalsFor}</div>
                  <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 4, margin: '0 16px', position: 'relative' }}>
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${(match.homeTeamStats.goalsFor / (match.homeTeamStats.goalsFor + match.awayTeamStats.goalsFor)) * 100}%`,
                      background: '#10b981',
                      borderRadius: 4
                    }} />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6' }}>{match.awayTeamStats.goalsFor}</div>
                </div>
              </div>

              {/* Goals Conceded */}
              <div style={{ background: '#f9fafb', borderRadius: 12, padding: '16px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>Goals Conceded</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>{match.homeTeamStats.goalsAgainst}</div>
                  <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 4, margin: '0 16px', position: 'relative' }}>
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${(match.homeTeamStats.goalsAgainst / (match.homeTeamStats.goalsAgainst + match.awayTeamStats.goalsAgainst)) * 100}%`,
                      background: '#ef4444',
                      borderRadius: 4
                    }} />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>{match.awayTeamStats.goalsAgainst}</div>
                </div>
              </div>

              {/* Win Percentage */}
              <div style={{ background: '#f9fafb', borderRadius: 12, padding: '16px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>Win Percentage</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#8b5cf6' }}>
                    {Math.round((match.homeTeamStats.wins / (match.homeTeamStats.wins + match.homeTeamStats.draws + match.homeTeamStats.losses)) * 100)}%
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>vs</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#8b5cf6' }}>
                    {Math.round((match.awayTeamStats.wins / (match.awayTeamStats.wins + match.awayTeamStats.draws + match.awayTeamStats.losses)) * 100)}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>💡</span> Key Insights
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {h2hData.insights.map((insight, idx) => (
                <div key={idx} style={{
                  background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                  padding: isMobile ? '12px' : '16px',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'start',
                  gap: 12
                }}>
                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#8b5cf6',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0
                  }}>
                    {idx + 1}
                  </div>
                  <p style={{ fontSize: isMobile ? 13 : 14, color: '#4c1d95', margin: 0, lineHeight: 1.6 }}>
                    {insight}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Prediction Comparison Component
  const PredictionComparisonTable = () => {
    // Get Gameweek 11 matches with results
    const gameweek11WithResults = getFixturesForGameweek(11)
      .filter(match => match.actualResult)
      .map(match => {
        // Generate predictions for these matches
        const matchWithPrediction = generatePrediction(match);
        return matchWithPrediction;
      });

    // Combine Gameweek 10 and Gameweek 11 matches with results
    const allCompletedMatches = [...completedMatchesWithPredictions, ...gameweek11WithResults];
    
    const gw10Completed = completedMatchesWithPredictions.length;
    const gw11Completed = gameweek11WithResults.length;
    const gw11Total = getFixturesForGameweek(11).length;

    return (
      <div style={{ marginBottom: 32 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12
        }}>
          <div>
            <h2 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#1f2937', margin: 0, marginBottom: 4 }}>
              🤖 AI Prediction Analysis
            </h2>
            <div style={{ fontSize: isMobile ? 12 : 14, color: '#6b7280' }}>
              Gameweek 10: {gw10Completed} matches • Gameweek 11: {gw11Completed}/{gw11Total} completed
            </div>
          </div>
          <button
            onClick={() => setShowPredictionComparison(!showPredictionComparison)}
            style={{
              padding: '8px 16px',
              background: showPredictionComparison ? '#ef4444' : branding.primaryColor,
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {showPredictionComparison ? 'Hide Analysis' : 'Show Analysis'}
          </button>
        </div>

        {showPredictionComparison && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {allCompletedMatches.map((match) => {
              const isPredictionCorrect = match.prediction?.mostLikely === match.actualResult?.winner;
              const failureAnalysis = !isPredictionCorrect ? analyzePredictionFailure(match) : '';

              return (
                <div key={match.id} style={{
                  background: '#ffffff',
                  borderRadius: 16,
                  padding: isMobile ? '16px' : '24px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  border: `2px solid ${isPredictionCorrect ? '#10b981' : '#ef4444'}`
                }}>
                  {/* Match Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                    flexWrap: 'wrap',
                    gap: 12
                  }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1f2937', margin: 0 }}>
                          {match.homeTeam} vs {match.awayTeam}
                        </h3>
                        <span style={{
                          padding: '2px 8px',
                          background: '#e0e7ff',
                          color: '#4338ca',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600
                        }}>
                          GW{match.gameWeek}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {match.date} • {match.venue}
                      </div>
                    </div>
                    <div style={{
                      padding: '6px 16px',
                      background: isPredictionCorrect 
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: '#ffffff',
                      borderRadius: 20,
                      fontSize: isMobile ? 12 : 14,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      {isPredictionCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </div>
                  </div>

                  {/* Comparison Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: 16,
                    marginBottom: isPredictionCorrect ? 0 : 20
                  }}>
                    {/* AI Prediction */}
                    <div style={{
                      background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                      padding: isMobile ? '16px' : '20px',
                      borderRadius: 12
                    }}>
                      <div style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#6b21a8',
                        marginBottom: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <FaBrain /> AI Prediction
                      </div>
                      
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: '#6b21a8', marginBottom: 4 }}>Predicted Winner</div>
                        <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#4c1d95' }}>
                          {match.prediction?.mostLikely === 'home' ? match.homeTeam :
                           match.prediction?.mostLikely === 'away' ? match.awayTeam :
                           'Draw'}
                        </div>
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: '#6b21a8', marginBottom: 4 }}>Predicted Score</div>
                        <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#4c1d95' }}>
                          {match.prediction?.predictedScore}
                        </div>
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: '#6b21a8', marginBottom: 4 }}>Confidence</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            flex: 1,
                            height: 8,
                            background: '#e5e7eb',
                            borderRadius: 4,
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${match.prediction?.confidence}%`,
                              height: '100%',
                              background: 'linear-gradient(90deg, #8b5cf6 0%, #6b21a8 100%)',
                              borderRadius: 4
                            }} />
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#4c1d95' }}>
                            {match.prediction?.confidence}%
                          </span>
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: '#6b21a8', marginBottom: 4 }}>Probabilities</div>
                        <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                          <div>H: <strong>{match.prediction?.homeWin}%</strong></div>
                          <div>D: <strong>{match.prediction?.draw}%</strong></div>
                          <div>A: <strong>{match.prediction?.awayWin}%</strong></div>
                        </div>
                      </div>
                    </div>

                    {/* Actual Result */}
                    <div style={{
                      background: isPredictionCorrect 
                        ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                        : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                      padding: isMobile ? '16px' : '20px',
                      borderRadius: 12
                    }}>
                      <div style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: isPredictionCorrect ? '#065f46' : '#991b1b',
                        marginBottom: 12
                      }}>
                        ⚽ Actual Result
                      </div>
                      
                      <div style={{ marginBottom: 12 }}>
                        <div style={{
                          fontSize: 12,
                          color: isPredictionCorrect ? '#065f46' : '#991b1b',
                          marginBottom: 4
                        }}>
                          Match Winner
                        </div>
                        <div style={{
                          fontSize: isMobile ? 18 : 20,
                          fontWeight: 700,
                          color: isPredictionCorrect ? '#047857' : '#7f1d1d'
                        }}>
                          {match.actualResult?.winner === 'home' ? match.homeTeam :
                           match.actualResult?.winner === 'away' ? match.awayTeam :
                           'Draw'}
                        </div>
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <div style={{
                          fontSize: 12,
                          color: isPredictionCorrect ? '#065f46' : '#991b1b',
                          marginBottom: 4
                        }}>
                          Final Score
                        </div>
                        <div style={{
                          fontSize: isMobile ? 18 : 20,
                          fontWeight: 700,
                          color: isPredictionCorrect ? '#047857' : '#7f1d1d'
                        }}>
                          {match.actualResult?.finalScore}
                        </div>
                      </div>

                      <div>
                        <div style={{
                          fontSize: 12,
                          color: isPredictionCorrect ? '#065f46' : '#991b1b',
                          marginBottom: 8
                        }}>
                          Key Match Events
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {match.actualResult?.matchEvents.map((event, idx) => (
                            <div key={idx} style={{
                              fontSize: 11,
                              color: isPredictionCorrect ? '#047857' : '#7f1d1d',
                              display: 'flex',
                              gap: 6
                            }}>
                              <span>•</span>
                              <span>{event}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Failure Analysis */}
                  {!isPredictionCorrect && failureAnalysis && (
                    <div style={{
                      background: '#fff7ed',
                      padding: isMobile ? '16px' : '20px',
                      borderRadius: 12,
                      border: '2px solid #fed7aa'
                    }}>
                      <div style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: '#c2410c',
                        marginBottom: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        🔍 Why This Prediction Failed - Detailed Analysis
                      </div>
                      <div style={{
                        fontSize: isMobile ? 13 : 14,
                        lineHeight: 1.7,
                        color: '#9a3412',
                        whiteSpace: 'pre-line'
                      }}>
                        {failureAnalysis.split('\n\n').map((paragraph, idx) => {
                          // Extract bold text
                          const parts = paragraph.split('**');
                          return (
                            <p key={idx} style={{ marginBottom: 12 }}>
                              {parts.map((part, i) => 
                                i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                              )}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Overall Accuracy */}
            <div style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              padding: isMobile ? '16px' : '24px',
              borderRadius: 16,
              border: '2px solid #0ea5e9'
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#0c4a6e', marginBottom: 12 }}>
                📈 Overall AI Prediction Accuracy
              </div>
              <div style={{ fontSize: isMobile ? 13 : 14, color: '#075985', lineHeight: 1.6 }}>
                <strong>Gameweek 10 Performance:</strong> {completedMatchesWithPredictions.filter(m => m.prediction?.mostLikely === m.actualResult?.winner).length} out of {completedMatchesWithPredictions.length} predictions correct 
                ({Math.round((completedMatchesWithPredictions.filter(m => m.prediction?.mostLikely === m.actualResult?.winner).length / completedMatchesWithPredictions.length) * 100)}% accuracy)
                <br/><br/>
                <strong>Gameweek 11 Performance (In Progress):</strong> {gameweek11WithResults.filter(m => m.prediction?.mostLikely === m.actualResult?.winner).length} out of {gw11Completed} completed predictions correct 
                ({gw11Completed > 0 ? Math.round((gameweek11WithResults.filter(m => m.prediction?.mostLikely === m.actualResult?.winner).length / gw11Completed) * 100) : 0}% accuracy) • {gw11Total - gw11Completed} matches remaining
                <br/><br/>
                <strong>Combined Performance:</strong> {allCompletedMatches.filter(m => m.prediction?.mostLikely === m.actualResult?.winner).length} out of {allCompletedMatches.length} total predictions correct 
                ({Math.round((allCompletedMatches.filter(m => m.prediction?.mostLikely === m.actualResult?.winner).length / allCompletedMatches.length) * 100)}% overall accuracy)
                <br/><br/>
                Our AI model continues to learn from each match outcome, incorporating new data points to improve future predictions. 
                Factors like red cards, injuries, and exceptional individual performances remain challenging to predict but are being integrated into our evolving algorithms.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const LiveGameCard = ({ game }: { game: typeof liveGames[0] }) => (
    <div style={{
      background: '#ffffff',
      borderRadius: 16,
      padding: 20,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb',
      position: 'relative',
      cursor: 'pointer'
    }}>
      <div style={{
        position: 'absolute',
        top: 12,
        right: 12,
        padding: '4px 8px',
        background: '#ef4444',
        color: '#ffffff',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: 4
      }}>
        <span style={{
          width: 6,
          height: 6,
          background: '#ffffff',
          borderRadius: '50%',
          animation: 'pulse 2s infinite'
        }}></span>
        LIVE
      </div>
      <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 12 }}>{game.image}</div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', margin: '0 0 8px 0' }}>{game.name}</h3>
      <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>
        <div>Provider: {game.provider}</div>
        <div>Players: {game.players.toLocaleString()}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, fontSize: 12 }}>
        <span style={{ color: '#6b7280' }}>Min: <strong>{game.minBet}</strong></span>
        <span style={{ color: '#6b7280' }}>Max: <strong>{game.maxBet}</strong></span>
      </div>
      <button style={{
        width: '100%',
        padding: '10px',
        background: branding.primaryColor,
        color: '#ffffff',
        border: 'none',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer'
      }}>
        Join Table
      </button>
    </div>
  );

  return (
    <PageTransition>
      <div className="page-container" style={{
        padding: isMobile ? '12px' : '24px 32px',
        background: '#f5f7fb',
        minHeight: '100%',
        paddingBottom: isMobile ? '100px' : '32px',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}>
        <div style={{ maxWidth: isMobile ? '100%' : 1200, margin: '0 auto' }}>
          {/* Header */}
          <header style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#086afb', textTransform: 'uppercase', letterSpacing: 1 }}>
                Entertainment Hub
              </span>
              <h1 style={{ fontSize: isMobile ? 28 : 32, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                iGaming
              </h1>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', margin: 0 }}>
                Play casino games, bet on sports, and join live dealer tables
              </p>
            </div>
          </header>

          {/* iGaming Account Balance Panel */}
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: isMobile ? '16px' : '24px',
            marginBottom: 24,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: '2px solid #8b5cf6'
          }}>
            {igamingAccountLoading ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <FaSpinner className="animate-spin" style={{ fontSize: 24, color: branding.primaryColor }} />
                <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>Loading account...</div>
              </div>
            ) : igamingAccount && igamingAccount.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Account Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  paddingBottom: 16,
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: '#8b5cf6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <FaDice style={{ color: 'white', fontSize: 24 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: isMobile ? 18 : 20, color: '#0f172a', marginBottom: 4 }}>
                      iGaming Account
                    </div>
                    <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#8b5cf6', marginBottom: 4 }}>
                      {formatAmount(igamingAccount.balance, igamingAccount.currency)}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                      {igamingAccount.accountNumber}
                    </div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                  gap: isMobile ? 12 : 16
                }}>
                  <div style={{
                    background: '#f0fdf4',
                    padding: isMobile ? '12px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ fontSize: 11, color: '#15803d', marginBottom: 4, fontWeight: 600 }}>
                      Total Deposited
                    </div>
                    <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#10b981' }}>
                      {formatAmount(igamingAccount.totalDeposited, igamingAccount.currency)}
                    </div>
                  </div>
                  <div style={{
                    background: '#fef2f2',
                    padding: isMobile ? '12px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #fecaca'
                  }}>
                    <div style={{ fontSize: 11, color: '#991b1b', marginBottom: 4, fontWeight: 600 }}>
                      Total Withdrawn
                    </div>
                    <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#ef4444' }}>
                      {formatAmount(igamingAccount.totalWithdrawn, igamingAccount.currency)}
                    </div>
                  </div>
                  <div style={{
                    background: '#faf5ff',
                    padding: isMobile ? '12px' : '16px',
                    borderRadius: 12,
                    border: '1px solid #e9d5ff'
                  }}>
                    <div style={{ fontSize: 11, color: '#6b21a8', marginBottom: 4, fontWeight: 600 }}>
                      Total Wagered
                    </div>
                    <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#8b5cf6' }}>
                      {formatAmount(igamingAccount.totalWagered, igamingAccount.currency)}
                    </div>
                  </div>
                  <div style={{
                    background: (igamingAccount.totalWon - igamingAccount.totalLost) >= 0 ? '#f0fdf4' : '#fef2f2',
                    padding: isMobile ? '12px' : '16px',
                    borderRadius: 12,
                    border: (igamingAccount.totalWon - igamingAccount.totalLost) >= 0 ? '1px solid #bbf7d0' : '1px solid #fecaca'
                  }}>
                    <div style={{
                      fontSize: 11,
                      color: (igamingAccount.totalWon - igamingAccount.totalLost) >= 0 ? '#15803d' : '#991b1b',
                      marginBottom: 4,
                      fontWeight: 600
                    }}>
                      Net Win/Loss
                    </div>
                    <div style={{
                      fontSize: isMobile ? 16 : 18,
                      fontWeight: 700,
                      color: (igamingAccount.totalWon - igamingAccount.totalLost) >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {formatAmount(igamingAccount.totalWon - igamingAccount.totalLost, igamingAccount.currency)}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <button
                    onClick={() => {
                      setIGamingModalType('deposit');
                      setShowIGamingModal(true);
                    }}
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px 16px' : '14px 24px',
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: isMobile ? 14 : 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      transition: 'all 0.2s',
                      minWidth: isMobile ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#7c3aed'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#8b5cf6'}
                  >
                    <FaPlus /> Deposit
                  </button>
                  <button
                    onClick={() => {
                      setIGamingModalType('withdraw');
                      setShowIGamingModal(true);
                    }}
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px 16px' : '14px 24px',
                      background: 'white',
                      color: '#8b5cf6',
                      border: '2px solid #8b5cf6',
                      borderRadius: 12,
                      fontSize: isMobile ? 14 : 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      transition: 'all 0.2s',
                      minWidth: isMobile ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#8b5cf6';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.color = '#8b5cf6';
                    }}
                  >
                    <FaArrowDown /> Withdraw
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                  Create an iGaming account to start playing!
                </div>
                <button
                  onClick={() => {
                    setIGamingModalType('deposit');
                    setShowIGamingModal(true);
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <FaPlus /> Create Account
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: isMobile ? '12px' : '16px',
            marginBottom: 24,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: isMobile ? '10px 16px' : '12px 24px',
                  background: activeTab === tab.id ? branding.primaryColor : 'transparent',
                  color: activeTab === tab.id ? '#ffffff' : '#6b7280',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'all 0.2s'
                }}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Bar (for Casino tab) */}
          {activeTab === 'casino' && (
            <div style={{ marginBottom: 24 }}>
              <div style={{
                position: 'relative',
                background: '#ffffff',
                borderRadius: 12,
                padding: '12px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <FaSearch style={{ color: '#9ca3af', fontSize: 18 }} />
                <input
                  type="text"
                  placeholder="Search games, providers, or categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    fontSize: 16,
                    color: '#1f2937'
                  }}
                />
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div style={{
              padding: '16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 12,
              marginBottom: 24,
              color: '#dc2626'
            }}>
              <p style={{ margin: 0, fontSize: 14 }}>{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px 20px'
            }}>
              <FaSpinner className="animate-spin" style={{ fontSize: 32, color: branding.primaryColor }} />
            </div>
          )}

          {/* Tab Content */}
          <div>
            {activeTab === 'casino' && !loading && (
              <div>
                {/* Featured: Tetris Challenge */}
                {!showTetrisGame && tetrisConfig?.isEnabled && (
                  <div style={{
                    background: '#667eea',
                    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 16,
                    padding: isMobile ? '24px' : '32px',
                    marginBottom: 32,
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                    border: '2px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      justifyContent: 'space-between',
                      gap: 24
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'inline-block',
                          background: 'rgba(255,255,255,0.25)',
                          padding: '8px 16px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          marginBottom: 16,
                          color: '#ffffff',
                          border: '1px solid rgba(255,255,255,0.3)'
                        }}>
                          🎮 FEATURED ARCADE GAME
                        </div>
                        <h2 style={{
                          fontSize: isMobile ? 28 : 36,
                          fontWeight: 700,
                          marginBottom: 12,
                          lineHeight: 1.2,
                          color: '#ffffff'
                        }}>
                          Tetris Challenge
                        </h2>
                        <p style={{
                          fontSize: isMobile ? 14 : 16,
                          marginBottom: 20,
                          lineHeight: 1.6,
                          color: '#ffffff'
                        }}>
                          Stack blocks, clear lines, and win real money! Choose your difficulty, place your bet, and reach the target score to win up to 5x your stake!
                        </p>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 16,
                          marginBottom: 20
                        }}>
                          <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            padding: '12px 20px',
                            borderRadius: 12,
                            border: '1px solid rgba(255,255,255,0.25)'
                          }}>
                            <div style={{ fontSize: 12, marginBottom: 4, color: '#ffffff' }}>Easy Mode</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#ffffff' }}>1.5x Multiplier</div>
                          </div>
                          <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            padding: '12px 20px',
                            borderRadius: 12,
                            border: '1px solid rgba(255,255,255,0.25)'
                          }}>
                            <div style={{ fontSize: 12, marginBottom: 4, color: '#ffffff' }}>Medium Mode</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#ffffff' }}>3x Multiplier</div>
                          </div>
                          <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            padding: '12px 20px',
                            borderRadius: 12,
                            border: '1px solid rgba(255,255,255,0.25)'
                          }}>
                            <div style={{ fontSize: 12, marginBottom: 4, color: '#ffffff' }}>Hard Mode</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#ffffff' }}>5x Multiplier</div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <button
                          onClick={() => setShowTetrisGame(true)}
                          style={{
                            background: '#ffffff',
                            color: '#667eea',
                            padding: isMobile ? '14px 28px' : '16px 40px',
                            borderRadius: 12,
                            fontWeight: 700,
                            fontSize: isMobile ? 14 : 16,
                            border: '2px solid #ffffff',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
                            e.currentTarget.style.background = '#f8f9ff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                            e.currentTarget.style.background = '#ffffff';
                          }}
                        >
                          Play Now →
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tetris Game View */}
                {showTetrisGame && (
                  <TetrisGame
                    onBack={() => setShowTetrisGame(false)}
                    onBalanceUpdate={fetchIGamingAccount}
                  />
                )}

                {/* Regular Casino Games */}
                {!showTetrisGame && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: 20,
                    marginBottom: 32
                  }}>
                    {filteredCasinoGames.map(game => (
                      <GameCard key={game.id} game={game} />
                    ))}
                  </div>
                )}
                {!loading && filteredCasinoGames.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    background: '#ffffff',
                    borderRadius: 16,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
                    <h3 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                      No games found
                    </h3>
                    <p style={{ fontSize: 14, color: '#6b7280' }}>
                      {searchQuery ? 'Try adjusting your search query' : 'No games available at the moment'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'sportsbook' && !loading && (
              <div>
                {/* Gameweek Navigation & My Predictions Button */}
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: 16,
                  marginBottom: 24,
                  background: '#ffffff',
                  padding: isMobile ? '16px' : '20px',
                  borderRadius: 16,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                      onClick={() => setCurrentGameweek(prev => Math.max(1, prev - 1))}
                      disabled={currentGameweek <= 11}
                      style={{
                        padding: '8px 16px',
                        background: currentGameweek <= 11 ? '#e5e7eb' : branding.primaryColor,
                        color: currentGameweek <= 11 ? '#9ca3af' : '#ffffff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: currentGameweek <= 11 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <FaChevronLeft /> Prev Week
                    </button>
                    <div style={{
                      padding: '8px 20px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: '#fff',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 700
                    }}>
                      Gameweek {currentGameweek}
                    </div>
                    <button
                      onClick={() => setCurrentGameweek(prev => Math.min(38, prev + 1))}
                      disabled={currentGameweek >= 12}
                      style={{
                        padding: '8px 16px',
                        background: currentGameweek >= 12 ? '#e5e7eb' : branding.primaryColor,
                        color: currentGameweek >= 12 ? '#9ca3af' : '#ffffff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: currentGameweek >= 12 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      Next Week <FaChevronRight />
                    </button>
                  </div>
                  <button
                    onClick={() => setShowSavedPredictions(!showSavedPredictions)}
                    style={{
                      padding: '8px 16px',
                      background: showSavedPredictions ? '#10b981' : '#f59e0b',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <FaBookmark /> {showSavedPredictions ? 'Hide Saved' : `My Predictions (${savedPredictions.length})`}
                  </button>
                </div>

                {/* My Saved Predictions Section */}
                {showSavedPredictions && (
                  <div style={{ marginBottom: 32 }}>
                    <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', marginBottom: 20 }}>
                      📌 My Saved Predictions
                    </h2>
                    {savedPredictions.length > 0 ? (
                      savedPredictions.map(match => (
                        <div key={`${match.gameWeek}-${match.id}`} style={{ position: 'relative' }}>
                          <PremierLeagueMatchCard match={match} />
                          <button
                            onClick={() => handleDeletePrediction(match.id, match.gameWeek)}
                            style={{
                              position: 'absolute',
                              top: 16,
                              right: 16,
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: 8,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              zIndex: 10
                            }}
                          >
                            <FaTrash size={12} /> Delete
                          </button>
                        </div>
                      ))
                    ) : (
                      <div style={{
                        background: '#ffffff',
                        borderRadius: 16,
                        padding: 40,
                        textAlign: 'center',
                        border: '2px dashed #e5e7eb'
                      }}>
                        <p style={{ fontSize: 16, color: '#6b7280', margin: 0 }}>
                          No saved predictions yet. Generate predictions and save them for future reference!
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Prediction Comparison */}
                <PredictionComparisonTable />

                {/* Premier League Matches Section */}
                <div style={{ marginBottom: 32 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', margin: 0 }}>
                      ⚽ Premier League Matches
                    </h2>
                    <span style={{
                      padding: '4px 12px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: '#fff',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}>
                      AI Predictions Available
                    </span>
                  </div>
                  
                  {premierLeagueMatches.length > 0 ? (
                    premierLeagueMatches.map(match => (
                      <PremierLeagueMatchCard key={match.id} match={match} />
                    ))
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      background: '#ffffff',
                      borderRadius: 16,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ fontSize: 48, marginBottom: 16 }}>⚽</div>
                      <h3 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                        No Premier League matches scheduled
                      </h3>
                      <p style={{ fontSize: 14, color: '#6b7280' }}>
                        Check back soon for upcoming fixtures
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'live' && !loading && (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 20
                }}>
                  {liveGames.length > 0 ? (
                    liveGames.map(game => (
                      <LiveGameCard key={game.id} game={game} />
                    ))
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      background: '#ffffff',
                      borderRadius: 16,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      gridColumn: '1 / -1'
                    }}>
                      <div style={{ fontSize: 48, marginBottom: 16 }}>📺</div>
                      <h3 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                        No live games available
                      </h3>
                      <p style={{ fontSize: 14, color: '#6b7280' }}>
                        Live dealer tables will appear here when available
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Stats Modal */}
      {selectedMatchForStats && <StatsModal match={selectedMatchForStats} />}

      {/* iGaming Account Modal */}
      <IGamingAccountModal
        isOpen={showIGamingModal}
        onClose={() => setShowIGamingModal(false)}
        onSuccess={handleIGamingAccountSuccess}
        type={igamingModalType}
        accounts={accounts}
        igamingAccount={igamingAccount}
      />
    </PageTransition>
  );
};

export default IGaming;
